import React, { memo, useEffect, useMemo } from 'react'
import { GetServerSideProps, GetServerSidePropsContext, NextPage } from 'next'
import { Container, useProductQuery } from '@thirstycamel/ui'
import { useRouter } from 'next/router'
import Delay from 'react-delay'
import type { CoreProductEntity } from '@ts/core/src/modules/coreproduct/coreproduct.entity'
import type { ProductSchemaOffer } from '@ts/core/src/modules/coreproduct/coreproduct.objects'
import type { ReviewEntity } from '@ts/core/src/modules/review/review.entity'
import { RequestQueryBuilder } from '@nestjsx/crud-request'

import LayoutMain from '../../../../layout/LayoutMain'
import { useStore } from '../../../../store/hooks'
import SpecialsSection from '../../../../components/SpecialsSection'
import Advert from '../../../../components/Advert'
import ProductReviews from '../../../../components/ProductReviews'
import ProductOverview from '../../../../components/ProductOverview'
import { parseCookies } from 'nookies'
import { QueryCache } from 'react-query'
import { dehydrate } from 'react-query/hydration'
import type { DehydratedState } from 'react-query/hydration'
import { backend } from '../../../../utils/backend'
import imageProxy from '../../../../utils/imageProxy'
import { eventGA4, eventUA } from '../../../../utils/gtag'
import { useGoogleAnalytics } from '../../../../components/GoogleAnalyticsProvider'

const USED_IMAGE_SIZES = [1200, 400, 100]

import geoip from 'geoip-lite'
import requestIp from 'request-ip'
import { ProductBundleOverview } from '../../../../components/ProductBundleOverview'
import { ProductType } from '@ts/core/src/modules/coreproduct/coreproduct.types'
import { track } from '../../../../hooks/useKlaviyo'
import { getCanonicalProductPath } from '../../../../utils/productUrl'
import { shouldSkipRequestRedirect } from '../../../../utils/shouldSkipRequestRedirect'
import { buildProductSchema } from '../../../../utils/schema'

const tags = [
  { property: 'product:availability', content: 'in stock' },
  { property: 'product:price:currency', content: 'AUD' },
]

const stripTrailingSlash = (path: string) =>
  path !== '/' && path.endsWith('/') ? path.slice(0, -1) : path

const normalizePathname = (pathname: string) => {
  try {
    return stripTrailingSlash(decodeURI(pathname))
  } catch {
    return stripTrailingSlash(pathname)
  }
}

interface ProductPageProps {
  productSchemaOffer: ProductSchemaOffer | null
  productSchemaReviews: ReviewEntity[]
  dehydratedState?: DehydratedState
}

const Product: NextPage<ProductPageProps> = ({ productSchemaOffer, productSchemaReviews }) => {
  const router = useRouter()
  const { trackEvent } = useGoogleAnalytics()

  const selectedStore = useStore(s => s.store.selectedStore)
  const location = useStore(store => store.location)

  const slug = router.query.slug as string
  const pricelist = router.query.pricelist as string

  let {
    data: product,
    isFetching,
    isError,
    error,
  } = useProductQuery<CoreProductEntity>(
    {
      product: slug,
      store: selectedStore?.slug,
      region: selectedStore?.slug ? undefined : location.region,
      pricelist,
    },
    {
      cacheTime: 300000,
      retry: false,
      refetchOnWindowFocus: false,
    },
  )

  // Check if product doesn't exist
  const productNotFound = useMemo(() => {
    if (!slug) return false
    if (error || isError) return true
    if (!isFetching && !product) return true
    return false
  }, [slug, isFetching, isError, error, product, selectedStore?.slug])

  // // Check if product is unavailable - requiring redirect
  // const shouldRedirect = useMemo(() => {
  //   if (!product || isFetching) return false

  //   // Don't redirect if store has external URL - external stores use different shopping flow
  //   if (selectedStore?.externalURL) return false

  //   const isAvailable = product?.availabilityIndexes?.[0]?.available === true
  //   const availableInStore = isAvailable
  //   const availableClickAndCollect = selectedStore?.isPickupReady && isAvailable
  //   const availableDelivery = selectedStore?.isDeliveryReady && isAvailable

  //   return (!availableInStore && !availableClickAndCollect && !availableDelivery) || !selectedStore
  // }, [product, selectedStore, isFetching])

  // // Redirect to product-unavailable if product doesn't exist
  // useEffect(() => {
  //   if (productNotFound) {
  //     // Pass the current product path so we can redirect back
  //     const productPath = router.asPath.split('?')[0] // Remove query params
  //     router.replace(`/product-unavailable?productPath=${encodeURIComponent(productPath)}`)
  //   }
  // }, [productNotFound, router])

  // useEffect(() => {
  //   if (shouldRedirect) {
  //     // Pass the current product path so we can redirect back
  //     const productPath = router.asPath.split('?')[0] // Remove query params
  //     router.replace(`/product-unavailable?productPath=${encodeURIComponent(productPath)}`)
  //   }
  // }, [shouldRedirect, router])

  useEffect(() => {
    if (!product) return

    track('Viewed Product', {
      id: product.id,
      name: product.productGroup?.name,
      brand: product.productGroup?.brand?.name,
      category: product.productGroup?.categories?.map(cat => cat.name).join('/'),
      variant: product.factorDescription,
    })

    trackEvent('ec', 'setAction', 'detail')

    /* Send to Google Analytics */
    eventUA('view_item', {
      event_category: 'ecommerce',
      event_label: 'View product',
      non_interaction: true,
      items: [
        {
          id: product.id,
          name: product.productGroup?.name,
          brand: product.productGroup?.brand?.name,
          content_category: product.productGroup?.categories?.map(cat => cat.name).join('/'),
          price: (product.specialPricing?.price ?? (product.pricing?.price || 0)) / 100,
          variant: product.factorDescription,
        },
      ],
    })

    eventGA4('view_item', {
      currency: 'AUD',
      value: (product.specialPricing?.price ?? (product.pricing?.price || 0)) / 100,
      items: [
        {
          item_id: product.id,
          item_name: product.productGroup?.name,
          item_brand: product.productGroup?.brand?.name,
          item_category: product.productGroup?.categories?.map(cat => cat.name).join('/'),
          price: (product.specialPricing?.price ?? (product.pricing?.price || 0)) / 100,
          item_variant: product.factorDescription,
        },
      ],
    })

    /* Send to Facebook Pixel */
    if (typeof fbq !== 'undefined') {
      fbq?.('track', 'ViewContent', {
        content_ids: [product.id],
        content_category: product.productGroup?.categories?.map(cat => cat.name).join('/'),
        content_name: `${product.productGroup?.brand?.name} ${product.productGroup?.name}`,
        content_type: 'product',
      })
    }
  }, [product])

  const imagesToPreload = useMemo(() => {
    if (!product) return []

    let imagesArray = []

    product?.productGroup?.products?.forEach(curr => {
      const images = curr?.media?.forEach(i =>
        USED_IMAGE_SIZES.forEach(height => imagesArray.push(imageProxy(i.filename, { height }))),
      )
      imagesArray = imagesArray.concat(images)
    })

    return imagesArray
  }, [product])

  // // Return null immediately if redirecting or product not found
  // if (productNotFound || isFetching || !product) {
  //   return null
  // }

  // Create the canonical URL for this product
  const canonicalUrl = `https://www.thirstycamel.com.au${getCanonicalProductPath(product)}`

  // If the URL loaded doesn't match the canonical path, redirect to the canonical path.
  // (Primarily to handle outdated / non-standard product URLs.)
  useEffect(() => {
    if (!product) return
    if (!router.asPath) return

    const canonicalPath = getCanonicalProductPath(product)
    const currentUrl = new URL(router.asPath, 'https://www.thirstycamel.com.au')

    const currentPath = normalizePathname(currentUrl.pathname)
    const expectedPath = normalizePathname(canonicalPath)

    if (currentPath !== expectedPath) {
      router.replace(`${canonicalPath}${currentUrl.search}${currentUrl.hash}`)
    }
  }, [router, product])

  const isBundle = product?.type === ProductType.BUNDLE

  const _title = isBundle
    ? product?.bundle?.title
    : `${product?.productGroup.brand?.name} ${product?.productGroup.name}`
  const structuredData = product
    ? buildProductSchema(product, canonicalUrl, productSchemaOffer, productSchemaReviews)
    : undefined

  //const bundleWithNoPricing = product && !product.specialPricing && !product.pricing && isBundle

  return (
    <LayoutMain
      title={_title}
      noContainer
      ogImage={product?.media?.[0] && imageProxy(product.media[0].filename, { height: 1200 })}
      images={imagesToPreload}
      description={isBundle ? product?.bundle?.description : product?.description}
      tags={tags}
      canonicalUrl={canonicalUrl}
      structuredData={structuredData}
      //noIndex={bundleWithNoPricing}
    >
      {isBundle ? (
        <ProductBundleOverview product={product} isFetching={!product && isFetching} />
      ) : (
        <ProductOverview product={product} isFetching={!product && isFetching} />
      )}

      <Container size="lg" py={[12, , 12]}>
        <Delay wait={300}>
          <SpecialsSection
            slug={product?.productGroup?.categories?.[0]?.slug || 'beer'}
            title="More products"
          />
        </Delay>
      </Container>

      <Delay wait={500}>
        <Advert position="PRODUCT" />
      </Delay>

      <Delay wait={500}>
        <ProductReviews product={product} isFetching={!product && isFetching} />
      </Delay>
    </LayoutMain>
  )
}

export const getServerSideProps: GetServerSideProps<ProductPageProps> = async (
  context: GetServerSidePropsContext,
) => {
  const queryCache = new QueryCache()

  const ip = requestIp.getClientIp(context.req)
  const result = geoip.lookup(ip)
  let region = result?.region || 'VIC' // Default to VIC

  const validRegions = ['NSW', 'VIC', 'QLD', 'WA', 'SA', 'TAS', 'ACT', 'NT']

  if (!validRegions.includes(region)) {
    region = 'VIC'
  }

  const { selected_store } = parseCookies(context)

  const slugParam = context.params?.slug
  const pricelist = context.query?.pricelist as string

  if (typeof slugParam !== 'string') {
    return {
      props: {
        dehydratedState: dehydrate(queryCache),
        productSchemaOffer: null,
        productSchemaReviews: [],
      },
    }
  }

  const slug = slugParam
  const schemaPromise = backend<ProductSchemaOffer>(`/products/${slug}/schema`, {
    params: { region: 'VIC' },
  }).catch(() => null)

  const key: [string, any] = [
    `products/${slug}`,
    {
      params: { store: selected_store, region: selected_store ? undefined : region, pricelist },
    },
  ]

  try {
    await queryCache.prefetchQuery(key, backend, { cacheTime: 30000 })
  } catch (e) {}

  const product = queryCache.getQueryData<CoreProductEntity>(key)
  const productSchemaOffer = await schemaPromise
  let productSchemaReviews: ReviewEntity[] = []

  if (product?.productGroup?.id) {
    const reviewsQuery = RequestQueryBuilder.create({
      join: { field: 'productGroup' },
      filter: [
        { field: 'productGroup.id', operator: 'eq', value: product.productGroup.id },
        { field: 'published', operator: 'eq', value: true },
      ],
    })
      .sortBy({ field: 'createdAt', order: 'DESC' })
      .setLimit(5)
      .query()

    productSchemaReviews = await backend<{ data: ReviewEntity[] }>(`/reviews?${reviewsQuery}`)
      .then(response => response?.data || [])
      .catch(() => [])
  }

  if (product && !shouldSkipRequestRedirect(context.req?.url)) {
    const canonicalPath = getCanonicalProductPath(product)

    const requestedUrl = new URL(`https://www.thirstycamel.com.au${context.req?.url || ''}`)
    const requestedPath = normalizePathname(requestedUrl.pathname)
    const expectedPath = normalizePathname(canonicalPath)

    if (requestedPath !== expectedPath) {
      // Manual redirect (type-safe for older Next typings in this repo)
      // Preserve query string (e.g. pricelist) while standardising the path.
      const destination = `${canonicalPath}${requestedUrl.search}`

      context.res.statusCode = 301
      context.res.setHeader('Location', destination)
      context.res.end()

      return { props: { productSchemaOffer, productSchemaReviews } }
    }
  }

  return {
    props: {
      dehydratedState: dehydrate(queryCache),
      productSchemaOffer,
      productSchemaReviews,
    },
  }
}

export default memo(Product)
